export type InputKind = "keyboard" | "touch" | "xbox" | "playstation" | "pad";

export interface InputState {
  moveX: number;
  moveY: number;
  sprint: boolean;
}

const DEAD_ZONE = 7;
const ZOOM_THRESHOLD = 0.6;
const ZOOM_REPEAT_DELAY = 0.1;
const ZOOM_REPEAT_INTERVAL = 0.11;
const DRAG_RADIUS = 52;
const SPRINT_AT = 1.6;

function padKind(pad: Gamepad): InputKind {
  const id = pad.id.toLowerCase();
  if (id.includes("xbox") || id.includes("045e")) return "xbox";
  if (
    id.includes("dualsense") ||
    id.includes("dualshock") ||
    id.includes("playstation") ||
    id.includes("wireless controller") ||
    id.includes("054c")
  ) {
    return "playstation";
  }
  return "pad";
}

interface Point {
  x: number;
  y: number;
}

export class Controls {
  readonly state: InputState = { moveX: 0, moveY: 0, sprint: false };

  private readonly held = new Set<string>();
  private orbitDelta = 0;
  private zoomDelta = 1;

  private moveTouch: number | null = null;
  private moveOrigin: Point = { x: 0, y: 0 };
  private touchVector: Point = { x: 0, y: 0 };
  private touchSprint = false;
  private padIndex: number | null = null;
  private padMove: { x: number; y: number } | null = null;
  private padSprint = false;
  private padZoomDirection = 0;
  private padZoomHold = 0;
  private padZoomRepeat = 0;
  private zoomSteps = 0;
  private padInteract = false;
  private padInventory = false;
  private padConfirmHeld = false;
  private padConfirmations = 0;
  private padNavX = 0;
  private padNavY = 0;
  private readonly dpad = [false, false, false, false];
  private inventories = 0;
  private dismisses = 0;
  // a phone should never be told to press E, so touch devices start as touch
  private lastInput: InputKind =
    window.matchMedia?.("(hover: none) and (pointer: coarse)").matches ||
    navigator.maxTouchPoints > 0
      ? "touch"
      : "keyboard";
  private pinchDistance = 0;
  private pinchX = 0;
  private tapAt = 0;
  private tapDragged = false;
  private pendingTap: Point | null = null;

  private readonly onKeyDown = (event: KeyboardEvent): void => {
    if (event.repeat) return;
    this.lastInput = "keyboard";
    this.held.add(event.code);
    if (event.code === "KeyI") this.inventories += 1;
    if (event.code === "Escape") this.dismisses += 1;
    if (event.code.startsWith("Arrow")) event.preventDefault();
    this.refresh();
  };

  private readonly onKeyUp = (event: KeyboardEvent): void => {
    this.held.delete(event.code);
    this.refresh();
  };

  private readonly onBlur = (): void => {
    this.held.clear();
    this.releaseMove();
    this.refresh();
  };

  private readonly onWheel = (event: WheelEvent): void => {
    event.preventDefault();
    this.zoomDelta *= Math.pow(1.0016, event.deltaY);
  };

  private readonly onTouchStart = (event: TouchEvent): void => {
    event.preventDefault();
    this.lastInput = "touch";
    if (event.touches.length >= 2) {
      this.tapDragged = true;
      this.releaseMove();
      this.beginPinch(event);
      this.refresh();
      return;
    }
    if (this.moveTouch !== null) return;
    const touch = event.changedTouches.item(0);
    if (!touch) return;
    this.moveTouch = touch.identifier;
    this.moveOrigin = { x: touch.clientX, y: touch.clientY };
    this.tapAt = performance.now();
    this.tapDragged = false;
    this.touchVector = { x: 0, y: 0 };
    this.touchSprint = false;
    this.refresh();
  };

  private readonly onTouchMove = (event: TouchEvent): void => {
    event.preventDefault();
    if (event.touches.length >= 2) {
      this.updatePinch(event);
      return;
    }
    const touch = this.findTouch(event.touches, this.moveTouch);
    if (!touch) return;

    const dx = touch.clientX - this.moveOrigin.x;
    const dy = touch.clientY - this.moveOrigin.y;
    const magnitude = Math.hypot(dx, dy);

    if (magnitude < DEAD_ZONE) {
      this.touchVector = { x: 0, y: 0 };
      this.touchSprint = false;
    } else {
      this.tapDragged = true;
      this.touchVector = { x: dx / magnitude, y: -dy / magnitude };
      this.touchSprint = magnitude > DRAG_RADIUS * SPRINT_AT;
    }
    this.refresh();
  };

  private readonly onTouchEnd = (event: TouchEvent): void => {
    const ended = !this.findTouch(event.touches, this.moveTouch);
    if (ended && this.moveTouch !== null) {
      if (!this.tapDragged && performance.now() - this.tapAt < 280) {
        this.pendingTap = { x: this.moveOrigin.x, y: this.moveOrigin.y };
      }
      this.releaseMove();
    }
    if (event.touches.length < 2) this.pinchDistance = 0;
    this.refresh();
  };

  private readonly onGamepadConnected = (event: GamepadEvent): void => {
    this.padIndex = event.gamepad.index;
  };

  private readonly onGamepadDisconnected = (event: GamepadEvent): void => {
    if (this.padIndex === event.gamepad.index) {
      this.padIndex = null;
      this.padMove = null;
      this.padZoomDirection = 0;
      this.padInteract = false;
      this.padInventory = false;
      this.zoomSteps = 0;
    }
  };

  private readonly onClick = (event: MouseEvent): void => {
    if (event.detail === 0) return;
    this.pendingTap = { x: event.clientX, y: event.clientY };
  };

  attach(target: HTMLElement): void {
    window.addEventListener("keydown", this.onKeyDown);
    window.addEventListener("keyup", this.onKeyUp);
    window.addEventListener("blur", this.onBlur);
    target.addEventListener("wheel", this.onWheel, { passive: false });
    target.addEventListener("touchstart", this.onTouchStart, { passive: false });
    target.addEventListener("touchmove", this.onTouchMove, { passive: false });
    target.addEventListener("touchend", this.onTouchEnd, { passive: false });
    target.addEventListener("touchcancel", this.onTouchEnd, { passive: false });
    window.addEventListener("gamepadconnected", this.onGamepadConnected);
    window.addEventListener("gamepaddisconnected", this.onGamepadDisconnected);
    if (navigator.maxTouchPoints === 0) {
      target.addEventListener("click", this.onClick);
    }
  }

  consumeDismiss(): number {
    const value = this.dismisses;
    this.dismisses = 0;
    return value;
  }

  consumePadNav(): { x: number; y: number } {
    const value = { x: this.padNavX, y: this.padNavY };
    this.padNavX = 0;
    this.padNavY = 0;
    return value;
  }

  consumePadConfirm(): number {
    const value = this.padConfirmations;
    this.padConfirmations = 0;
    return value;
  }

  consumeInventory(): number {
    const value = this.inventories;
    this.inventories = 0;
    return value;
  }

  inputKind(): InputKind {
    return this.lastInput;
  }

  /**
   * Hold-to-confirm source for the world action. A finger resting still on the
   * world counts as a hold; dragging it is movement, not intent.
   */
  isInteractDown(): boolean {
    if (this.held.has("KeyE")) return true;
    if (this.padInteract) return true;
    return this.moveTouch !== null && !this.tapDragged;
  }

  consumeTap(): Point | null {
    const tap = this.pendingTap;
    this.pendingTap = null;
    return tap;
  }

  /**
   * Polled, not pushed: the Gamepad API exposes no button events in Chrome, so
   * reading the snapshot each frame is the only mechanism. It costs nothing
   * until a pad reports itself connected.
   */
  pollGamepad(delta: number): void {
    if (this.padIndex === null) return;
    const pads = navigator.getGamepads?.() ?? [];
    const pad = pads[this.padIndex];
    if (!pad || !pad.connected) {
      this.padIndex = null;
      this.padMove = null;
      return;
    }

    const deadzone = 0.18;
    const rawX = pad.axes[0] ?? 0;
    const rawY = pad.axes[1] ?? 0;
    const magnitude = Math.hypot(rawX, rawY);
    if (magnitude < deadzone) {
      this.padMove = null;
      this.padSprint = false;
    } else {
      const scaled = Math.min(1, (magnitude - deadzone) / (1 - deadzone));
      this.padMove = { x: (rawX / magnitude) * scaled, y: (rawY / magnitude) * scaled };
      this.padSprint = magnitude > 0.85;
    }

    this.padInteract = pad.buttons[0]?.pressed ?? false;
    const packPressed = pad.buttons[1]?.pressed ?? false;
    if (packPressed && !this.padInventory) this.inventories += 1;
    this.padInventory = packPressed;

    // A doubles as confirm: while a panel is up it drives the cursor, not the world
    const confirm = pad.buttons[0]?.pressed ?? false;
    if (confirm && !this.padConfirmHeld) this.padConfirmations += 1;
    this.padConfirmHeld = confirm;

    // d-pad steps, one per press, so it does not run away when held
    const pad12 = pad.buttons[12]?.pressed ?? false;
    const pad13 = pad.buttons[13]?.pressed ?? false;
    const pad14 = pad.buttons[14]?.pressed ?? false;
    const pad15 = pad.buttons[15]?.pressed ?? false;
    if (pad12 && !this.dpad[0]) this.padNavY -= 1;
    if (pad13 && !this.dpad[1]) this.padNavY += 1;
    if (pad14 && !this.dpad[2]) this.padNavX -= 1;
    if (pad15 && !this.dpad[3]) this.padNavX += 1;
    this.dpad[0] = pad12;
    this.dpad[1] = pad13;
    this.dpad[2] = pad14;
    this.dpad[3] = pad15;
    if (this.padInteract || pad.buttons.some((button) => button.pressed) || magnitude > 0.2) {
      this.lastInput = padKind(pad);
    }

    // right stick drives zoom: one step on the push, then auto-repeats while held.
    // non-standard pads sometimes put RY on axis 4, so fall back to whichever is
    // deflected more.
    const primary = pad.axes[3] ?? 0;
    const secondary = pad.mapping === "standard" ? 0 : (pad.axes[4] ?? 0);
    const stick = Math.abs(primary) >= Math.abs(secondary) ? primary : secondary;
    const direction = stick < -ZOOM_THRESHOLD ? -1 : stick > ZOOM_THRESHOLD ? 1 : 0;

    if (direction !== this.padZoomDirection) {
      this.padZoomDirection = direction;
      this.padZoomHold = 0;
      this.padZoomRepeat = 0;
      if (direction !== 0) this.zoomSteps += direction;
    } else if (direction !== 0) {
      this.padZoomHold += delta;
      if (this.padZoomHold > ZOOM_REPEAT_DELAY) {
        this.padZoomRepeat += delta;
        while (this.padZoomRepeat >= ZOOM_REPEAT_INTERVAL) {
          this.padZoomRepeat -= ZOOM_REPEAT_INTERVAL;
          this.zoomSteps += direction;
        }
      }
    }

    this.refresh();
  }

  consumeZoomSteps(): number {
    const value = this.zoomSteps;
    this.zoomSteps = 0;
    return value;
  }

  consumeOrbit(): number {
    const value = this.orbitDelta;
    this.orbitDelta = 0;
    return value;
  }

  consumeZoom(): number {
    const value = this.zoomDelta;
    this.zoomDelta = 1;
    return value;
  }

  private beginPinch(event: TouchEvent): void {
    const a = event.touches.item(0);
    const b = event.touches.item(1);
    if (!a || !b) return;
    this.pinchDistance = Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY);
    this.pinchX = (a.clientX + b.clientX) / 2;
  }

  private updatePinch(event: TouchEvent): void {
    const a = event.touches.item(0);
    const b = event.touches.item(1);
    if (!a || !b) return;
    const distance = Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY);
    if (this.pinchDistance > 0 && distance > 0) {
      this.zoomDelta *= this.pinchDistance / distance;
    }
    this.pinchDistance = distance;

    const midX = (a.clientX + b.clientX) / 2;
    this.orbitDelta += (midX - this.pinchX) * 0.005;
    this.pinchX = midX;
  }

  private findTouch(list: TouchList, id: number | null): Touch | null {
    if (id === null) return null;
    for (let i = 0; i < list.length; i++) {
      const touch = list.item(i);
      if (touch && touch.identifier === id) return touch;
    }
    return null;
  }

  private releaseMove(): void {
    this.moveTouch = null;
    this.touchVector = { x: 0, y: 0 };
    this.touchSprint = false;
  }

  private refresh(): void {
    const held = this.held;
    let x = 0;
    let y = 0;
    if (held.has("ArrowLeft") || held.has("KeyA")) x -= 1;
    if (held.has("ArrowRight") || held.has("KeyD")) x += 1;
    if (held.has("ArrowUp") || held.has("KeyW")) y += 1;
    if (held.has("ArrowDown") || held.has("KeyS")) y -= 1;

    if (held.has("BracketLeft")) this.orbitDelta -= 0.06;
    if (held.has("BracketRight")) this.orbitDelta += 0.06;

    const length = Math.hypot(x, y);
    let moveX = length > 0 ? x / length : 0;
    let moveY = length > 0 ? y / length : 0;
    let sprint = held.has("ShiftLeft") || held.has("ShiftRight");

    if (this.moveTouch !== null) {
      moveX = this.touchVector.x;
      moveY = this.touchVector.y;
      sprint = this.touchSprint;
    }

    if (this.padMove !== null) {
      moveX = this.padMove.x;
      moveY = -this.padMove.y;
      sprint = this.padSprint;
    }

    this.state.moveX = moveX;
    this.state.moveY = moveY;
    this.state.sprint = sprint;
  }
}
